import { declareAction } from "./declareAction";
import { createCtx, Tree } from "./kernel";
import { assign, equals, getIsAction, getName, getOwnKeys, getTree, nameToId, safetyFunc, throwError, TREE, } from "./shared";
const DEPS = Symbol("@@Reatom/DEPS");
const DEPS_SHAPE = Symbol("@@Reatom/DEPS_SHAPE");
// action for set initialState of each atom to global state
export const init = declareAction(["@@Reatom/init"]);
export const initAction = init();
export function declareAtom(name, initialState, dependencyMatcher) {
    if (!dependencyMatcher) {
        dependencyMatcher = initialState;
        initialState = name;
        name = "atom";
    }
    const _id = nameToId(name);
    const _name = getName(_id);
    if (initialState === undefined)
        throwError(`Atom "${_name}". Initial state can't be undefined`);
    const _tree = new Tree(_id);
    const _deps = new Set();
    // start from `0` for missing `actionDefault`
    let dependencePosition = 0;
    const initialPhase = true;
    function on(dep, reducer) {
        if (!initialPhase)
            throwError("Can't define dependencies after atom initialization");
        safetyFunc(reducer, "reducer");
        const position = dependencePosition++;
        const depTree = getTree(dep);
        if (!depTree)
            throwError("Invalid dependency");
        const depId = depTree.id;
        const isDepActionCreator = getIsAction(dep);
        _tree.union(depTree);
        const update = function update({ state, stateNew, payload, changedIds, type }) {
            const atomStateSnapshot = state[_id];
            // first `walk` of lazy (dynamically added by subscription) atom
            const isAtomLazy = atomStateSnapshot === undefined;
            if (!isAtomLazy && type === initAction.type && !payload)
                return;
            const atomStatePreviousReducer = stateNew[_id];
            // it is mean atom has more than one dependencies
            // that depended from dispatched action
            // and one of the atom reducers already processed
            const hasAtomNewState = atomStatePreviousReducer !== undefined;
            const atomState = (hasAtomNewState ? atomStatePreviousReducer : atomStateSnapshot);
            const depStateSnapshot = state[depId];
            const depStateNew = stateNew[depId];
            const isDepChanged = depStateNew !== undefined;
            const depState = isDepChanged ? depStateNew : depStateSnapshot;
            const depValue = isDepActionCreator ? payload : depState;
            if (isDepActionCreator || isDepChanged || isAtomLazy) {
                const atomStateNew = reducer(atomState, depValue);
                if (atomStateNew === undefined)
                    throwError(`Invalid state. Reducer number ${position} in "${_name}" atom returns undefined`);
                if (hasAtomNewState && equals(atomStateSnapshot, atomStateNew)) {
                    changedIds.splice(changedIds.indexOf(_id), 1);
                    delete stateNew[_id];
                    return;
                }
                if (!equals(atomState, atomStateNew)) {
                    if (!hasAtomNewState)
                        changedIds.push(_id);
                    stateNew[_id] = atomStateNew;
                }
            }
        };
        update._ownerAtomId = _id;
        if (isDepActionCreator)
            return _tree.addFn(update, depId);
        if (_deps.has(depId))
            throwError("One of dependencies has the equal id");
        _deps.add(depId);
        depTree.fnsMap.forEach((_, key) => _tree.addFn(update, key));
    }
    on(init, (_, { [_id]: state = initialState } = {}) => state);
    dependencyMatcher(on);
    const atom = function atom(state = {}, action = initAction) {
        const ctx = createCtx(state, action);
        _tree.forEach(action.type, ctx);
        const { changedIds, stateNew } = ctx;
        return changedIds.length > 0 ? assign({}, state, stateNew) : state;
    };
    atom[TREE] = _tree;
    atom[DEPS] = _deps;
    return atom;
}
export function getState(state, atom) {
    return state[atom[TREE].id];
}
export function map(name, source, mapper) {
    if (!mapper) {
        mapper = source;
        source = name;
        name = Symbol(`${getName(getTree(source).id)} [map]`);
    }
    safetyFunc(mapper, "mapper");
    return declareAtom(name, 
    // FIXME: initialState for `map` :thinking:
    null, (handle) => 
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    handle(source, (_state, payload) => mapper(payload)));
}
export function combine(name, shape) {
    if (arguments.length === 1)
        shape = name;
    const isArray = Array.isArray(shape);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const keys = getOwnKeys(shape);
    if (arguments.length === 1)
        name = isArray
            ? Symbol(`[${keys.map((k) => getName(getTree(shape[k]).id)).join()}]`)
            : Symbol(`{${keys.map(getName).join()}}`);
    const atom = declareAtom(name, isArray ? [] : {}, (reduce) => keys.forEach((key) => reduce(shape[key], (state, payload) => {
        const newState = isArray ? state.slice(0) : assign({}, state);
        newState[key] = payload;
        return newState;
    })));
    atom[DEPS_SHAPE] = shape;
    return atom;
}
export function getDepsShape(thing) {
    return thing[DEPS_SHAPE];
}
